import { lazy, Suspense, useEffect, useState } from "react";
import { OperationalWorkspace } from "../App";
import { api } from "../api/client";
import type {
  AuthSession,
  IntegratedShellAccess,
} from "../api/types";
import {
  integratedWorkspaceFromLocation,
  integratedWorkspaceHref,
  type IntegratedWorkspaceId,
} from "../integratedRoute";
import { errorMessage } from "../utils/errors";
import { FieldWorkApp, isHomeStyleFieldRole } from "./FieldWorkApp";
import "./integrated-shell.css";

const ApprovedHomeStyleWorkspace = lazy(async () => ({
  default: (await import("./ApprovedHomeStyleWorkspace")).ApprovedHomeStyleWorkspace,
}));

interface IntegratedShellProps {
  session: AuthSession;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
}

export function hasIntegratedShellCapability(session: AuthSession): boolean {
  return session.capabilities?.includes("INTEGRATED_SHELL_READ") === true;
}

function LoadingState() {
  return (
    <main className="integrated-shell-state" aria-label="통합 셸 불러오는 중">
      <i className="spinner" />
      <strong>Project Selmo 작업공간 접근 범위를 확인하고 있습니다.</strong>
    </main>
  );
}

function BlockedState({ message }: { message: string }) {
  return (
    <main className="integrated-shell-state integrated-shell-state--blocked" role="alert">
      <span aria-hidden="true">!</span>
      <h1>통합 셸 접근이 차단되었습니다</h1>
      <p>{message}</p>
      <a className="button button--secondary" href="/">기존 시공 Workspace로 돌아가기</a>
    </main>
  );
}

function IntegratedEmployeeShell({ session, loggingOut, onLogout }: IntegratedShellProps) {
  const [workspace, setWorkspace] = useState<IntegratedWorkspaceId>(
    () => integratedWorkspaceFromLocation(window.location.pathname, window.location.hash),
  );
  const [access, setAccess] = useState<IntegratedShellAccess>();
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string>();
  const capable = hasIntegratedShellCapability(session);

  useEffect(() => {
    const nextWorkspace = integratedWorkspaceFromLocation(window.location.pathname, window.location.hash);
    setWorkspace(nextWorkspace);
    const canonicalPath = integratedWorkspaceHref(nextWorkspace);
    if (window.location.pathname !== canonicalPath) {
      const constructionHash = nextWorkspace === "construction" ? "#/dashboard" : "";
      window.history.replaceState(
        window.history.state,
        "",
        `${canonicalPath}${window.location.search}${constructionHash}`,
      );
    }
  }, []);

  useEffect(() => {
    if (workspace === "construction") {
      setAccess(undefined);
      setLoading(false);
      setFailure(undefined);
      return;
    }
    if (!capable) {
      setLoading(false);
      setFailure("현재 세션에는 Project Selmo 통합 작업공간 조회 권한이 없습니다.");
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setFailure(undefined);
    void api.integratedShellAccess(controller.signal).then((nextAccess) => {
      if (nextAccess.capability !== "INTEGRATED_SHELL_READ" || nextAccess.readOnly !== true) {
        throw new Error("서버가 통합 셸 capability를 확인하지 못했습니다.");
      }
      setAccess(nextAccess);
    }).catch((error) => {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setAccess(undefined);
        setFailure(errorMessage(error));
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [capable, workspace]);

  useEffect(() => {
    if (workspace !== "home-style" || !capable || !access) return;
    const verify = () => {
      void api.integratedShellAccess().then((nextAccess) => {
        if (nextAccess.capability !== "INTEGRATED_SHELL_READ" || nextAccess.readOnly !== true) {
          throw new Error("서버가 통합 셸 capability를 철회했습니다.");
        }
        setAccess(nextAccess);
      }).catch((error) => {
        setAccess(undefined);
        setFailure(errorMessage(error));
      });
    };
    const timer = window.setInterval(verify, 5_000);
    return () => window.clearInterval(timer);
  }, [Boolean(access), capable, workspace]);

  if (workspace === "construction") {
    return (
      <OperationalWorkspace
        session={session}
        loggingOut={loggingOut}
        onLogout={onLogout}
      />
    );
  }
  if (!capable) return <BlockedState message={failure || "허용되지 않은 세션입니다."} />;
  if (loading) return <LoadingState />;
  if (!access || failure) return <BlockedState message={failure || "통합 셸 kill switch가 꺼져 있습니다."} />;

  return (
    <Suspense fallback={<LoadingState />}>
      <ApprovedHomeStyleWorkspace
        session={session}
        loggingOut={loggingOut}
        onLogout={onLogout}
        pilotProjectCount={access.pilotProjectCount}
      />
    </Suspense>
  );
}

export function IntegratedShell(props: IntegratedShellProps) {
  if (isHomeStyleFieldRole(props.session.role)) {
    return <FieldWorkApp {...props} />;
  }
  return <IntegratedEmployeeShell {...props} />;
}
